import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Clock, RefreshCcw, Plus, Trash2, ChevronDown, Star, Check, X, Calendar, Package, TrendingUp, AlertCircle, DollarSign } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import PipelineView from "../components/PipelineView";
import AdminQuoteCard from "../components/AdminQuoteCard";
import AdminContentEditor from "../components/admin/AdminContentEditor";
import AdminServicesManager from "../components/admin/AdminServicesManager";
import AdminPortfolioManager from "../components/admin/AdminPortfolioManager";
import { api } from "../lib/api";

const STATUS_OPTIONS = ["new", "reviewing", "in_progress", "completed", "rejected"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("pipeline");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, q, r, fb] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/quotes"),
        api.get("/admin/ratings"),
        api.get("/admin/site-feedback"),
      ]);
      setStats(s.data); setQuotes(q.data); setRatings(r.data); setFeedback(fb.data);
    } catch (e) {
      setError("Failed to load dashboard data. Please refresh.");
      console.error("Dashboard load error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    load();
  };

  const toggleApprove = async (id, approved) => {
    const { data } = await api.patch(`/admin/ratings/${id}`, { approved });
    setRatings((rs) => rs.map((r) => (r.id === id ? data : r)));
  };

  const deleteRating = async (id) => {
    await api.delete(`/admin/ratings/${id}`);
    setRatings((rs) => rs.filter((r) => r.id !== id));
  };

  const filtered = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A]">
      <Navbar />
      <section className="pt-36 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ admin · control room</p>
              <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight">
                Mission <span className="text-gradient-cyan">Control</span>.
              </h1>
              <p className="text-[#5A5A72] mt-3">All customer requests, in one secure place.</p>
            </div>
            <button onClick={load} data-testid="admin-refresh-btn" className="btn-ghost px-4 py-2.5 rounded-lg text-sm flex items-center gap-2">
              <RefreshCcw className="w-4 h-4" /> Refresh
            </button>
          </div>

          {error && (
            <div className="mb-6 glass-card rounded-xl p-4 border border-red-500/30" data-testid="admin-error">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid md:grid-cols-5 gap-4 mb-10">
            {stats && [
              { l: "Total", v: stats.total_quotes },
              { l: "New", v: stats.new_quotes },
              { l: "In Progress", v: stats.in_progress },
              { l: "Completed", v: stats.completed },
              { l: "Customers", v: stats.customers },
            ].map((s) => (
              <motion.div
                key={s.l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`admin-stat-${s.l.toLowerCase().replace(" ", "-")}`}
                className="glass-card rounded-xl p-5"
              >
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5A5A72]">{s.l}</p>
                <p className="font-display font-black text-3xl mt-2 text-gradient-cyan">{s.v}</p>
              </motion.div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-black/[0.08]">
            {[
              { id: "pipeline", label: "Delivery Pipeline" },
              { id: "quotes", label: "Quote Requests" },
              { id: "content", label: "Site Content" },
              { id: "services", label: "Services" },
              { id: "portfolio", label: "Work" },
              { id: "ratings", label: `Ratings ${ratings.length ? `(${ratings.length})` : ""}` },
              { id: "feedback", label: `Site Feedback ${feedback.length ? `(${feedback.length})` : ""}` },
            ].map((t) => (
              <button
                key={t.id} onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`px-5 py-3 text-sm font-medium border-b-2 transition ${tab === t.id ? "border-[#2060EE] text-[#0E0E1A] font-semibold" : "border-transparent text-[#5A5A72] hover:text-[#0E0E1A]"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "pipeline" && (
            <PipelineView quotes={quotes} onDeliverySet={load} />
          )}

          {tab === "content" && <AdminContentEditor />}
          {tab === "services" && <AdminServicesManager />}
          {tab === "portfolio" && <AdminPortfolioManager />}

          {tab === "quotes" && (
            <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {["all", ...STATUS_OPTIONS].map((s) => (
              <button
                key={s} onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition ${filter === s ? "border-[#00D4FF]/60 bg-[#00D4FF]/10 text-[#00D4FF]" : "border-black/[0.08] text-[#5A5A72] hover:border-white/20"}`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>

          {/* Quotes */}
          {loading ? (
            <Loader label="LOADING MISSION DATA..." />
          ) : filtered.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center" data-testid="admin-empty">
              <p className="font-display text-lg">No requests in this view.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((q, i) => (
                <AdminQuoteCard key={q.id} quote={q} index={i} onStatusChange={updateStatus} onProgressUpdated={load} />
              ))}
            </div>
          )}
            </>
          )}

          {tab === "ratings" && (
            <div className="space-y-4">
              {ratings.length === 0 ? (
                <div className="glass-card rounded-2xl p-12 text-center" data-testid="admin-ratings-empty">
                  <Star className="w-10 h-10 mx-auto text-[#5A5A72] mb-3" />
                  <p className="font-display text-lg">No ratings yet.</p>
                  <p className="text-[#6B7280] text-sm mt-1">Customers will rate from milestone emails.</p>
                </div>
              ) : (
                ratings.map((r, i) => (
                  <motion.div
                    key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    data-testid={`admin-rating-${r.id}`}
                    className="glass-card rounded-2xl p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, n) => (
                              <Star key={`star-${n}`} className={`w-4 h-4 ${n < r.stars ? "fill-yellow-400 text-yellow-400" : "text-slate-700"}`} />
                            ))}
                          </div>
                          {r.approved ? (
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 px-2 py-1 rounded-full">Live on landing</span>
                          ) : (
                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5A5A72] bg-white/5 border border-black/[0.08] px-2 py-1 rounded-full">Pending</span>
                          )}
                        </div>
                        <p className="font-display font-bold mt-3">{r.customer_name || "Anonymous"}</p>
                        <p className="text-xs text-[#00D4FF] font-mono uppercase tracking-wider mt-0.5">
                          {r.service_type ? `${r.service_type} · ` : ""}{r.project_title || ""}
                        </p>
                        {r.comment ? (
                          <p className="text-[#4B5563] mt-3 leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                        ) : (
                          <p className="text-[#6B7280] italic text-sm mt-3">Customer didn't leave a written comment.</p>
                        )}
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B7280] mt-3">
                          {new Date(r.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        {r.comment && (
                          <button
                            onClick={() => toggleApprove(r.id, !r.approved)}
                            data-testid={`admin-rating-toggle-${r.id}`}
                            className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${r.approved ? "btn-ghost" : "btn-primary"}`}
                          >
                            {r.approved ? <><X className="w-3.5 h-3.5" /> Unpublish</> : <><Check className="w-3.5 h-3.5" /> Publish</>}
                          </button>
                        )}
                        <button
                          onClick={() => deleteRating(r.id)}
                          data-testid={`admin-rating-delete-${r.id}`}
                          className="px-4 py-2 rounded-lg text-xs btn-ghost flex items-center gap-1.5 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {tab === "feedback" && (
            <div className="space-y-4">
              {feedback.length === 0 ? (
                <div className="glass-card rounded-2xl p-12 text-center" data-testid="admin-feedback-empty">
                  <Star className="w-10 h-10 mx-auto text-[#5A5A72] mb-3" />
                  <p className="font-display text-lg">No site feedback yet.</p>
                  <p className="text-[#6B7280] text-sm mt-1">Visitor feedback from the widget will appear here.</p>
                </div>
              ) : (
                feedback.map((fb, i) => (
                  <motion.div
                    key={fb.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    data-testid={`admin-feedback-${fb.id}`}
                    className="glass-card rounded-2xl p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                          <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, n) => (
                              <Star key={`star-${n}`} className={`w-4 h-4 ${n < fb.stars ? "fill-yellow-400 text-yellow-400" : "text-slate-700"}`} />
                            ))}
                          </div>
                          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/20 px-2 py-1 rounded-full">
                            {fb.feedback_type || "website"}
                          </span>
                          {fb.page && (
                            <span className="text-[10px] font-mono text-[#6B7280]">
                              from {fb.page}
                            </span>
                          )}
                        </div>
                        {fb.comment ? (
                          <p className="text-[#4B5563] leading-relaxed">{fb.comment}</p>
                        ) : (
                          <p className="text-[#6B7280] italic text-sm">No comment left.</p>
                        )}
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B7280] mt-3">
                          {new Date(fb.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
